import { useState } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  type AdminUser,
  type RoleLabels,
  type UserAgreement,
  type UserNote,
} from '../lib/api.js';
import { audienceLabel } from '../lib/agreement-audience.js';
import { roleLabel, splitRoles } from '../lib/roles.js';
import { count } from '../lib/format.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { RecordScreen, resolveTab, type RecordTabs } from '../components/Record.js';
import { InfoBubble } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import { UserAccessTab } from './UserAccess.js';
import { UserSubscriptionsTab } from './UserSubscriptions.js';
import { UserFamilyTab } from './UserFamily.js';
import { AgeBadge } from '../components/AgeBand.js';
import { BirthDatePanel } from './UserBirthDate.js';
import { DeliveryText, StatusPill } from '../components/NotificationStatus.js';
import { isNotFound, sendsPath } from '../lib/notifications.js';
import {
  AsyncBoundary,
  Avatar,
  Badge,
  Button,
  DotPill,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
  inputStyle,
} from '../components/ui.js';

/**
 * Everything about one account, in one place.
 *
 * ── What this replaces, twice ────────────────────────────────────────
 *
 * FIRST a panel that appeared BELOW the table when a row was selected, pushing
 * the list down and leaving the row you clicked somewhere off screen — four
 * stacked sub-panels, all open at once, on a screen that was already a table.
 *
 * THEN a five-tab modal, which fixed that and introduced a quieter problem: the
 * account was not a location. `#/users/abc123` opened this only if that account
 * was in the current page of results, so the Users screen carried a note reading
 * "That account is not in the current results. Clear the search to open it." An
 * id pasted into an incident channel opened for whoever had not searched yet.
 *
 * It is a SCREEN now (Jon, 2026-08-28) and it fetches the account by id, so the
 * link is the record. The note is gone with it. See components/Record.tsx.
 *
 * ── What is deliberately NOT here ────────────────────────────────────
 *
 * The account's own settings — password, MFA, display name. Settled in #63:
 * the user portal is a separate service for end users (#10) and this is not it.
 * The old portal carried those because it was also the account UI, and undoing
 * that conflation is the point rather than an omission.
 *
 * IMPERSONATION is absent because the capability is switched off in the service,
 * not because the button was skipped. Both endpoints are in DENIED_ADMIN_PATHS
 * (auth: lib/admin-only.ts) and 404 for everyone including a full admin, so a
 * button here would have nothing to call. Deferred indefinitely in auth#63.
 *
 * The `impersonated` badge further down is NOT left over from that. It reads
 * `impersonatedBy` off a session row, which is worth surfacing precisely because
 * the feature is meant to be off — a session carrying it would mean the deny
 * list has a hole, and this is the screen where that would be seen.
 */
const TABS = [
  { path: 'profile', label: 'Profile' },
  { path: 'access', label: 'Access' },
  /* Family Groups (org-admin#187): always present, with an empty state, so a
     link to an account's family never breaks. */
  { path: 'family', label: 'Family' },
  { path: 'sessions', label: 'Sessions' },
  { path: 'passkeys', label: 'Passkeys' },
  { path: 'notes', label: 'Notes' },
] as const satisfies RecordTabs;

/**
 * Subscriptions and promotional grants (RidgeWill/auth#540), for callers holding
 * `subscription:read`: Support, Auth and Global Admin. Absent for everyone else
 * rather than shown and refused, because the whole tab is one read.
 */
const TABS_WITH_SUBSCRIPTIONS = [...TABS, { path: 'subscriptions', label: 'Subscriptions' }] as const satisfies RecordTabs;

export function UserDetail({ id, tab }: { id: string; tab?: string }) {
  const { state, reload } = useAsync(() => adminApi.user(id), [id]);
  const maySeeSubscriptions = useCan('subscription:read');
  const tabs: RecordTabs = maySeeSubscriptions ? TABS_WITH_SUBSCRIPTIONS : TABS;
  const current = resolveTab(tabs, tab);

  return (
    <AsyncBoundary state={state}>
      {(user) => (
        <RecordScreen
          back={{ to: '/users', label: 'Users' }}
          /* The NAME, per the chip rule — except that an account's name is
             optional and its email is what staff actually say out loud, so the
             email is the heading and the display name sits under it. A closed
             account has neither: its PII is cleared, and saying so beats an
             empty heading. */
          avatar={<Avatar id={user.id} label={user.name || user.email || user.id} image={user.image} />}
          title={user.email ?? 'Closed account'}
          badges={
            <>
              <Status user={user} />
              {/* The age band on every tab (org-admin#187), so staff always
                  know whether they are dealing with a member under 18. */}
              <AgeBadge band={user.ageBand} />
            </>
          }
          description={user.name ?? undefined}
          actions={
            /* An ordinary link rather than a button that navigates: the activity
               feed scoped to this account is an address, and one worth being
               able to copy out of an incident channel. */
            <Button href={`#/activity?user=${encodeURIComponent(user.id)}`}>View activity</Button>
          }
          tabs={tabs}
          base={`/users/${encodeURIComponent(user.id)}`}
          current={current}
        >
          {/*
            ROLES ARE ON ACCESS, not here (RidgeWill/auth#146). A role is one of
            three things that decide what somebody can reach, and showing it
            apart from the other two is what made a group-inherited role
            indistinguishable from an assigned one.
          */}
          {current === 'profile' ? <Profile user={user} onChanged={reload} /> : null}
          {current === 'access' ? <UserAccessTab user={user} onChanged={reload} /> : null}
          {current === 'family' ? <UserFamilyTab user={user} /> : null}
          {current === 'sessions' ? <Sessions user={user} /> : null}
          {current === 'passkeys' ? <Passkeys userId={user.id} /> : null}
          {current === 'notes' ? <Notes userId={user.id} /> : null}
          {current === 'subscriptions' ? <UserSubscriptionsTab userId={user.id} /> : null}
        </RecordScreen>
      )}
    </AsyncBoundary>
  );
}

/**
 * The account itself, and whether it can sign in.
 *
 * ── The facts panel is new, and is what the screen bought ────────────
 *
 * In the modal this tab was the ban form and nothing else, because a dialog has
 * no room to spare and the header carried the identity. A screen does have the
 * room, and every one of these was previously answerable only by reading the
 * row in the list behind the dialog — which is the thing an admin has usually
 * navigated away from by the time they wonder.
 *
 * The ID IS SHOWN, deliberately. It is what the activity feed, the auth logs and
 * every support escalation are keyed by, and having to copy it out of the
 * address bar is how people end up pasting a truncated one.
 */
function Profile({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  return (
    <>
      <Panel title="Account">
        <PanelBody>
          <div style={s.facts}>
            <Fact label="Name">
              {user.name ?? <span style={{ color: C.muted }}>none set</span>}
            </Fact>
            <Fact label="Email verified">
              {user.emailVerified ? <Badge tone="good">yes</Badge> : <Badge tone="warn">no</Badge>}
            </Fact>
            <Fact label="Two-factor">
              {/* A missing column and a false one are the same answer — this
                  account does not have it — so both render as "not enabled"
                  rather than one of them as an empty cell. */}
              {user.twoFactorEnabled ? (
                <Badge tone="good">enabled</Badge>
              ) : (
                <Badge>not enabled</Badge>
              )}
            </Fact>
            <Fact label="Created">
              <When iso={user.createdAt} />
            </Fact>
            <Fact label="Account id">
              <code style={s.id}>{user.id}</code>
            </Fact>
          </div>
        </PanelBody>
      </Panel>

      {/*
        "Ban", not "Sign-in" — Jon, 2026-08-28.

        It was named for what the panel is ABOUT (whether this person can sign
        in) rather than for what the panel DOES, and on a screen of nouns that
        reads as a report until you find a red button in it. Every other panel
        here is named for its contents.
      */}
      {/* auth#672: the date itself, its history and support's correction. */}
      <BirthDatePanel user={user} onChanged={onChanged} />

      <Panel title="Ban">
        <PanelBody>
          <SignIn user={user} onChanged={onChanged} />
        </PanelBody>
      </Panel>

      <Agreements userId={user.id} />

      {user.email ? <RecentSends email={user.email} /> : null}
    </>
  );
}

const RECENT_SENDS = 5;

/**
 * The last few emails sent to this account's address (org-admin#131).
 *
 * For the question support gets on a call, "I never got the email", answered
 * without leaving the profile. The full history is one link away on the Sends
 * tab, filtered to the same address.
 *
 * BY ADDRESS, not by account: a send records who it went to, not which user,
 * so an address change means older sends to the previous address are not
 * listed here. A closed account has no address and no panel.
 *
 * Absent for a role without `notification:read`, rather than disabled: there is
 * nothing to act on, only a list the role cannot read.
 */
function RecentSends({ email }: { email: string }) {
  const allowed = useCan('notification:read');
  const { state } = useAsync(
    () =>
      allowed
        ? adminApi.notificationSends({ address: email, limit: RECENT_SENDS })
        : Promise.resolve({ items: [], nextCursor: null }),
    [email, allowed],
  );
  if (!allowed) return null;

  return (
    <Panel
      title="Recent emails"
      action={<Button href={`#${sendsPath(email)}`}>View all</Button>}
    >
      {state.status === 'failed' ? (
        isNotFound(state.message) ? (
          <PanelBody>
            <p style={s.muted}>
              This panel could not be read. It needs the notification read permission, and an auth
              service new enough to serve the Notifications admin routes.
            </p>
          </PanelBody>
        ) : (
          <Note tone="danger">{state.message}</Note>
        )
      ) : (
        <AsyncBoundary state={state}>
          {(d) =>
            d.items.length === 0 ? (
              <EmptyState
                title="No emails sent to this address"
                suggestion="Nothing from any Ridgewill app has been sent to it."
              />
            ) : (
              <Table columns={['When', 'App', 'Type', 'Status', 'Delivery']}>
                {d.items.map((send) => (
                  <tr key={send.id}>
                    <Td>
                      <a href={`#/notifications/sends/${encodeURIComponent(send.id)}`} style={{ color: C.text }}>
                        <When iso={send.createdAt} />
                      </a>
                    </Td>
                    <Td mono>{send.appSlug}</Td>
                    <Td mono>{send.type}</Td>
                    <Td>
                      <StatusPill status={send.status} reason={send.reason} />
                    </Td>
                    <Td>
                      <DeliveryText status={send.status} delivery={send.deliveryStatus} />
                    </Td>
                  </tr>
                ))}
              </Table>
            )
          }
        </AsyncBoundary>
      )}
    </Panel>
  );
}

/**
 * Where this account stands on everything it is subject to (Jon, 2026-08-28).
 *
 * -- The Agreements screen cannot answer this ------------------------
 *
 * It answers from the other end: one agreement, everybody who accepted it. That
 * is the shape a legal review wants. The question staff have on a call is the
 * transpose - this person is stuck, what do they still owe - and answering it
 * from the acceptance lists means opening every agreement in turn and searching
 * each one for the same account.
 *
 * SATISFIED ROWS ARE SHOWN, not filtered to what is outstanding. A list of only
 * outstanding items cannot tell "they have signed everything" from "nothing
 * applies to them", which are very different things to say to somebody.
 *
 * -- A 404 here is a ROLE, not a fault -------------------------------
 *
 * The endpoint is guarded by `agreement: ['read']`, which Legal Admin and Global
 * Admin hold and Auth Admin deliberately does not - publishing terms of service
 * is Legal's. So a perfectly valid Auth Admin session gets a refusal on this one
 * panel while every other panel on the screen works, and a red error box would
 * read as the portal being broken. It says what is actually true instead.
 *
 * `describe()` would otherwise render it as "this account probably lacks the
 * admin role", which is the right sentence for the whole API and the wrong one
 * for a single panel on a screen that has plainly loaded.
 *
 * ── The sentence covers TWO causes, because they are indistinguishable ──
 *
 * A refused permission and an endpoint that does not exist are both a bare 404
 * with no code, so the portal cannot tell them apart — and a portal deployed
 * ahead of RidgeWill/auth#285 would otherwise tell a Global Admin their role is
 * insufficient, which is both false and unfixable by them. Naming both causes is
 * the only honest thing the screen can say.
 */
function Agreements({ userId }: { userId: string }) {
  const { state } = useAsync(() => adminApi.userAgreements(userId), [userId]);
  /**
   * Guardians' names for the guardian half (org-admin#187), read only when some
   * row has one: the agreements payload carries the guardian's id, and the
   * family read is where that id has a name. A refusal there just leaves the
   * guardian unnamed; it never fails this panel.
   */
  const needsNames =
    state.status === 'ready' && state.data.agreements.some((a) => a.halves?.guardianId);
  const names = useAsync(
    () =>
      needsNames
        ? adminApi.userFamily(userId).then(
            (f) => new Map(f.memberships.flatMap((m) => m.group.members).map((p) => [p.id, p.name || p.email || ''])),
            () => new Map<string, string>(),
          )
        : Promise.resolve(new Map<string, string>()),
    [userId, needsNames],
  );
  const guardianNames = names.state.status === 'ready' ? names.state.data : new Map<string, string>();

  if (state.status === 'failed' && state.message.includes('404')) {
    return (
      <Panel title="Agreements">
        <PanelBody>
          <p style={s.muted}>
            This panel could not be read. It needs the agreements permission, which a Legal Admin
            and a Global Admin hold and an Auth Admin does not &mdash; and an auth service new
            enough to answer for one account rather than one agreement.
          </p>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <Panel
      title="Agreements"
      info={
        <InfoBubble label="What this list covers">
          Every agreement in force for the applications this account can reach, org-wide ones
          included. Read from the acceptance record rather than from the cache the applications gate
          on, so the two can disagree &mdash; and a difference is worth chasing, because it is
          somebody being asked to re-accept something they have already signed.
        </InfoBubble>
      }
    >
      <AsyncBoundary state={state}>
        {(d) =>
          d.agreements.length === 0 ? (
            <EmptyState
              title="Subject to nothing"
              suggestion="No agreement is in force for the applications this account can reach. That includes org-wide ones, so it means none has been published yet."
            />
          ) : (
            <>
              <Table columns={['Agreement', 'Scope', 'Version', 'Status', 'Signed']}>
                {d.agreements.map((a) => (
                  <tr key={a.agreementSlug}>
                    <Td>
                      {/* The row label carries the NAME with the slug beneath it
                          in monospace - the chip rule. */}
                      <div style={{ fontWeight: 600 }}>{a.name}</div>
                      <code style={{ fontSize: '.78rem', color: C.muted }}>{a.agreementSlug}</code>
                    </Td>
                    <Td>
                      {/* Never blank: that would read as missing data on the
                          field that decides who owes it. */}
                      <Badge>{audienceLabel(a)}</Badge>
                    </Td>
                    <Td>{a.version}</Td>
                    <Td>
                      <AgreementStatus row={a} />
                    </Td>
                    <Td>
                      {a.halves ? (
                        <Halves halves={a.halves} guardianNames={guardianNames} />
                      ) : a.acceptedAt ? (
                        <When iso={a.acceptedAt} />
                      ) : (
                        <span style={{ color: C.muted }}>&mdash;</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>

              {/*
                THE CACHE, only when it disagrees.

                `pendingAgreements` is what a consuming application actually
                gates on, and the table above is the record. Showing the cache
                every time would be a second list nobody reads; showing it when
                the two differ is the whole reason the server returns it - that
                is a person being asked to accept something the record says they
                have signed, which has no other symptom anywhere.
              */}
              <CacheDrift agreements={d.agreements} cache={d.pendingCache} />
            </>
          )
        }
      </AsyncBoundary>
    </Panel>
  );
}

/**
 * Accepted, withdrawn, or outstanding.
 *
 * Three states rather than a tick and a cross. `accepted: false` covers both
 * "has not got round to it" and "agreed and then took it back", and those are
 * different conversations - the second is the one an escalation is usually
 * about, and it is invisible if the two are painted the same colour.
 */
function AgreementStatus({ row }: { row: UserAgreement }) {
  if (row.accepted) return <DotPill tone="good">accepted</DotPill>;
  if (row.withdrawnAt) return <DotPill tone="danger">withdrawn</DotPill>;
  /* A member under 18 (auth#615): say which half is missing, because "outstanding"
     sends support to the member when it is the guardian who has not accepted. */
  if (row.halves?.self && !row.halves.guardian) return <DotPill tone="warn">waiting for guardian</DotPill>;
  if (row.halves?.guardian && !row.halves.self) return <DotPill tone="warn">waiting for member</DotPill>;
  return <DotPill tone="warn">outstanding</DotPill>;
}

/**
 * Both halves of one agreement for a member under 18 (org-admin#187): their own
 * acceptance and the guardian's, with the guardian named and linked.
 */
function Halves({
  halves,
  guardianNames,
}: {
  halves: NonNullable<UserAgreement['halves']>;
  guardianNames: Map<string, string>;
}) {
  const notYet = <span style={{ color: C.muted }}>not yet</span>;
  const row = { display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.84rem', whiteSpace: 'nowrap' as const };
  const label = { color: C.muted, minWidth: '4.6rem' };
  const guardian = halves.guardianId;
  return (
    <div style={{ display: 'grid', gap: '.2rem' }}>
      <div style={row}>
        <span style={label}>Member</span>
        {halves.self ? halves.selfAcceptedAt ? <When iso={halves.selfAcceptedAt} /> : 'accepted' : notYet}
      </div>
      <div style={row}>
        <span style={label}>Guardian</span>
        {halves.guardian && guardian ? (
          <>
            <a href={`#/users/${encodeURIComponent(guardian)}`} style={s.authorLink}>
              {guardianNames.get(guardian) || 'guardian'}
            </a>
            {halves.guardianAcceptedAt ? (
              <>
                <span style={{ color: C.muted }}>&middot;</span>
                <When iso={halves.guardianAcceptedAt} />
              </>
            ) : null}
          </>
        ) : (
          notYet
        )}
      </div>
    </div>
  );
}

/**
 * Exported and pure, because the whole value of this is in the comparison and
 * getting it backwards is silent.
 *
 * Two directions, and they mean opposite things:
 *
 *   IN THE CACHE, SIGNED IN THE RECORD  the applications are still asking. This
 *                                       is the one that generates support calls.
 *   OUTSTANDING, ABSENT FROM THE CACHE  the applications are not asking for
 *                                       something that is owed. Quieter, worse.
 *
 * Reporting only the first would hide the second, which is the direction with no
 * symptom at all.
 *
 * Only slugs the RECORD knows about are compared. A cache naming an agreement
 * that no longer applies to this account - retired, or scoped to an application
 * they have lost - is stale in a different and less interesting way, and
 * flagging it here would put a permanent warning on accounts nothing is wrong
 * with.
 */
export function cacheDrift(
  agreements: UserAgreement[],
  cache: string[],
): { staleInCache: string[]; missingFromCache: string[] } {
  const owed = new Set(agreements.filter((a) => !a.accepted).map((a) => a.agreementSlug));
  const known = new Set(agreements.map((a) => a.agreementSlug));
  return {
    staleInCache: cache.filter((slug) => known.has(slug) && !owed.has(slug)),
    missingFromCache: [...owed].filter((slug) => !cache.includes(slug)),
  };
}

function CacheDrift({ agreements, cache }: { agreements: UserAgreement[]; cache: string[] }) {
  const { staleInCache, missingFromCache } = cacheDrift(agreements, cache);
  if (staleInCache.length === 0 && missingFromCache.length === 0) return null;

  return (
    <PanelBody>
      <Note tone="warn">
        The cache the applications read disagrees with the record.{' '}
        {staleInCache.length > 0 ? (
          <>
            They are still being asked for <strong>{staleInCache.join(', ')}</strong>, which this
            account has signed.{' '}
          </>
        ) : null}
        {missingFromCache.length > 0 ? (
          <>
            Nothing is asking them for <strong>{missingFromCache.join(', ')}</strong>, which is
            outstanding.{' '}
          </>
        ) : null}
        The cache is rebuilt when a role or a group membership changes, so a change to either will
        settle it.
      </Note>
    </PanelBody>
  );
}

export function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gap: '.2rem' }}>
      <span style={s.factLabel}>{label}</span>
      <span style={{ fontSize: '.88rem' }}>{children}</span>
    </div>
  );
}

export function Status({ user }: { user: AdminUser }) {
  if (user.pseudonymized) return <DotPill tone="muted">closed</DotPill>;
  if (user.banned) return <DotPill tone="danger">banned</DotPill>;
  if (!user.emailVerified) return <DotPill tone="warn">unverified</DotPill>;
  return <DotPill tone="good">active</DotPill>;
}

/**
 * Checkboxes built from `/admin/roles`, not a text field.
 *
 * That endpoint exists precisely so the portal does not carry a copy that
 * drifts, and free text would reintroduce the problem in a worse form:
 * `/admin/set-role` stores what it is given, so a typo produces an account
 * holding a role nothing grants — no error, no entitlement, and nothing on the
 * row to say it is misspelled rather than new.
 */
/**
 * What a "Save roles" click should send, or why it should not be sent (#28).
 *
 * Exported and pure so the multi-role case has a test. That matters more than
 * usual here: the bug was invisible to the single-role path, which is the one
 * anybody verifying a fix tries first, so the assertion has to be specifically
 * about two.
 *
 * ── The array is the fix, and the comma was a reasonable mistake ─────
 *
 * The previous code sent `picked.join(',')`, reasoning that the server stores
 * one comma-separated column. True of the COLUMN, not of the wire:
 * `/admin/set-role` validates each entry before it splits, so the joined string
 * is looked up whole, matches no role, and every multi-role save is refused.
 *
 * ── Why an unlisted role is refused here rather than by the server ──
 *
 * The server does refuse it, and it is the one that decides — this is a
 * message, not a control. But its refusal arrives EMPTY: the plugin throws
 * `APIError.from('BAD_REQUEST', ADMIN_ERROR_CODES.…)` where that constant is a
 * plain string, while `APIError.from` reads `.code` and `.message` off its
 * second argument. Both come out `undefined`, so the portal can only show
 * `400 Bad Request` — which is precisely what made this bug expensive to read.
 *
 * We already fetched the server's own list to draw the checkboxes, so we can
 * name the offending value instead of relaying a blank.
 */
export type RoleSavePlan =
  | { ok: true; role: string[]; message: string }
  | { ok: false; problem: string };

export function planRoleSave(picked: string[], known: string[]): RoleSavePlan {
  const unlisted = picked.filter((r) => !known.includes(r));
  if (unlisted.length > 0) {
    return {
      ok: false,
      problem:
        `The server does not list ${unlisted.join(', ')} as a role, and will refuse the save. ` +
        `Untick ${unlisted.length === 1 ? 'it' : 'them'} first.`,
    };
  }
  return {
    ok: true,
    role: picked,
    message: picked.length ? `Roles set to ${picked.join(', ')}.` : 'All roles removed.',
  };
}

/**
 * Banning and reinstating.
 *
 * Called `Access` until the Access TAB arrived and took the name. The rename is
 * the honest one: this section has never been about what somebody can reach, it
 * is about whether they can sign in at all — and two things called Access, one
 * of which is a ban button, is how somebody revokes an account looking for a
 * checkbox.
 */
function SignIn({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('');
  const action = useAction();

  return (
    <div style={s.section}>
      <OutcomeNote outcome={action.outcome} />

      {user.banned ? (
        <div style={{ display: 'grid', gap: '.6rem' }}>
          {user.banReason ? (
            <p style={s.muted}>Reason on file: {user.banReason}</p>
          ) : null}
          {user.banExpires ? (
            <p style={s.muted}>
              Expires <When iso={user.banExpires} />.
            </p>
          ) : null}
          <p style={s.muted}>Reinstating restores sign-in. Old sessions are not restored.</p>
          <div>
            <Button
              disabled={action.running}
              onClick={() =>
                void action
                  .run(async () => {
                    await adminApi.unbanUser(user.id);
                    return 'Reinstated.';
                  })
                  .then(onChanged)
              }
            >
              {action.running ? 'Working…' : 'Reinstate'}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '.7rem' }}>
          <Field
            label="Reason"
            hint="Kept on the account for staff. Required rather than optional: a ban nobody can explain is a ban nobody will lift."
          >
            <input value={reason} onChange={(e) => setReason(e.target.value)} style={inputStyle} />
          </Field>
          <Field
            label="Expires after (days)"
            /* The server supports `banExpiresIn` and the portal only ever set
               permanent bans. A temporary suspension that nobody has to remember
               to lift is the difference between a cooling-off period and an
               account quietly abandoned. */
            hint="Leave blank for a permanent ban."
          >
            <input
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              placeholder="permanent"
              style={inputStyle}
            />
          </Field>
          <div>
            <HoldButton
              title="Hold for a second and a half to ban this account. Their sessions end at once."
              disabled={action.running || !reason.trim()}
              onHold={() =>
                void action
                  .run(async () => {
                    const seconds = days ? Number(days) * 24 * 60 * 60 : undefined;
                    await adminApi.banUser(user.id, reason.trim(), seconds);
                    setReason('');
                    setDays('');
                    return seconds
                      ? `Banned for ${days} days. Their sessions are revoked by the server.`
                      : 'Banned. Their sessions are revoked by the server.';
                  })
                  .then(onChanged)
              }
            >
              {action.running ? 'Working…' : 'Ban'}
            </HoldButton>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sessions, which the portal could not see at all.
 *
 * `listUserSessions` and `revokeUserSessions` have existed the whole time, and
 * `session.revoked` is already a user-visible activity type — so the person it
 * happens to is told. Revoking is the proportionate response to "their laptop
 * was stolen", where banning the account is not.
 */
function Sessions({ user }: { user: AdminUser }) {
  const { state, reload } = useAsync(() => adminApi.userSessions(user.id), [user.id]);
  const action = useAction();

  return (
    <Panel
      title="Active sessions"
      action={
        <HoldButton
          title="Hold for a second and a half to sign this account out everywhere."
          disabled={action.running}
          onHold={() =>
            void action
              .run(async () => {
                await adminApi.revokeUserSessions(user.id);
                return 'All sessions revoked. They sign in again; the account is not banned.';
              })
              .then(() => reload())
          }
        >
          {action.running ? 'Working…' : 'Revoke all'}
        </HoldButton>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}

      <AsyncBoundary state={state}>
        {(d) =>
          d.sessions.length === 0 ? (
            <EmptyState
              title="No active sessions"
              suggestion="They are signed out everywhere, or every session has expired."
            />
          ) : (
            <Table columns={['Started', 'Expires', 'IP', 'Device']}>
              {d.sessions.map((sess) => (
                <tr key={sess.id}>
                  <Td>
                    <When iso={sess.createdAt} />
                  </Td>
                  <Td>
                    <When iso={sess.expiresAt} />
                  </Td>
                  <Td mono>{sess.ipAddress ?? '—'}</Td>
                  <Td>
                    <span style={s.agent} title={sess.userAgent ?? undefined}>
                      {sess.userAgent ?? '—'}
                    </span>
                    {sess.impersonatedBy ? <Badge tone="warn">impersonated</Badge> : null}
                  </Td>
                </tr>
              ))}
            </Table>
          )
        }
      </AsyncBoundary>
    </Panel>
  );
}

/**
 * The passkeys on an account. org-admin#29.
 *
 * ── There is no remove button, and that is the decision ──────────────
 *
 * Jon, 2026-08-25: staff read this and nothing more. The support question is
 * informational — "have you set one up, and when" — and a user who has lost
 * their device recovers themselves with a reset-password link. Stripping
 * somebody's strongest credential is not a thing this portal can do.
 *
 * That was spelled out on the panel itself for a while, on the reasoning that a
 * staff member hunting for a control they cannot find assumes it is broken. Jon
 * removed it on 2026-08-25: a paragraph above every passkey table, restating a
 * policy, is read once and then becomes furniture. The absence of a button is
 * its own statement, and the recovery path belongs in support guidance rather
 * than in a box on a screen. Kept here so the reasoning survives the copy.
 */
function Passkeys({ userId }: { userId: string }) {
  const { state } = useAsync(() => adminApi.passkeys(userId), [userId]);

  return (
    <Panel title="Passkeys">
      <AsyncBoundary state={state}>
        {(d) =>
          d.passkeys.length === 0 ? (
            <EmptyState
              title="No passkeys"
              suggestion="This account signs in with a password or a linked provider."
            />
          ) : (
            <Table columns={['Authenticator', 'Name', 'Added', 'Last used', 'Backup']}>
              {d.passkeys.map((key) => (
                <tr key={key.id}>
                  <Td>
                    <Authenticator name={key.authenticator} aaguid={key.aaguid} />
                  </Td>
                  <Td>
                    {/* NOT reliably user-typed, and nothing here can tell which
                        it is. The account portal names a new passkey from the
                        browser's user-agent — `describe()` in its Sessions
                        section, producing "Windows · Chrome" — and offers that
                        as a pre-filled rename. Somebody who closes the tab keeps
                        the guess. So the tooltip covers both cases rather than
                        crediting the user with a string a script wrote. */}
                    <span title="Named by the account holder, or guessed from their browser when the passkey was created">
                      {key.name ?? 'Unnamed'}
                    </span>
                  </Td>
                  <Td>
                    <When iso={key.createdAt} />
                  </Td>
                  <Td>
                    <LastUsed iso={key.lastUsedAt} />
                  </Td>
                  <Td>
                    {key.backedUp ? (
                      <Badge>synced</Badge>
                    ) : (
                      <Badge tone="warn">this device only</Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )
        }
      </AsyncBoundary>
    </Panel>
  );
}

/**
 * WHAT the passkey is, as distinct from what it was called.
 *
 * The authenticator asserts its own model at registration, so a resolved name
 * is a fact — unlike `name`, which may be a browser guess the owner never
 * edited. This is the column a support conversation actually wants: "have you
 * set one up?" is answered better by "1Password" than by "Windows · Chrome".
 *
 * ── Unidentified is the COMMON case, and must not read as broken ─────
 *
 * Apple zeroes the AAGUID under the default attestation flow auth uses, so
 * iCloud Keychain and Apple Passwords — likely the most common authenticators
 * here — resolve to nothing. A staff member seeing a blank must not conclude
 * something is wrong with the passkey, so the cell says which kind of unknown
 * it is and shows the raw identifier when there is one to report.
 */
function Authenticator({ name, aaguid }: { name: string | null; aaguid: string | null }) {
  const label = describeAuthenticator(name, aaguid);
  if (label.identified) return <span>{label.text}</span>;
  return (
    <span style={s.muted} title={label.title}>
      {label.text}
    </span>
  );
}

/**
 * Which KIND of unknown an unidentified authenticator is.
 *
 * Exported and pure so the three cases are pinned by a test rather than by
 * review, for the same reason as `lastUsedLabel`: the whole risk in this column
 * is a staff member reading a blank as a fault in somebody's credential and
 * saying so on a call. They are genuinely different situations and only one of
 * them is worth anybody's attention.
 */
export function describeAuthenticator(
  name: string | null,
  aaguid: string | null,
): { text: string; title?: string; identified: boolean } {
  if (name) return { text: name, identified: true };

  // The all-zero AAGUID, which is a deliberate anonymisation rather than a gap.
  // Dashes stripped first so the check is "every digit is a zero" rather than a
  // regex a reader has to simulate.
  const digits = aaguid?.replace(/-/g, '') ?? '';
  if (digits.length > 0 && /^0+$/.test(digits)) {
    return {
      identified: false,
      text: 'Not identified',
      title: `This authenticator does not identify its model. Apple devices do this by default, so an Apple passkey normally looks like this. Reported as ${aaguid}.`,
    };
  }

  if (aaguid) {
    return {
      identified: false,
      text: 'Unrecognised',
      title: `Not a model we have a name for. Report ${aaguid} if this comes up often, and it can be added.`,
    };
  }

  return {
    identified: false,
    text: 'Not recorded',
    title: 'Registered before this was recorded, so there is nothing to identify it with.',
  };
}

/**
 * "Last used", including the case where we genuinely do not know.
 *
 * The column arrived on 2026-08-25 (RidgeWill/auth#154) and is null on every
 * passkey registered before it. Null therefore means NOT RECORDED, which is a
 * different statement from "never used" — a passkey somebody uses daily reads
 * as null here until their next sign-in.
 *
 * Rendering it as "Never" would be the portal asserting something false, and
 * the person acting on it is a staff member advising a user about a credential.
 * A dash alone would be honest but unreadable, so it says which it is.
 */
function LastUsed({ iso }: { iso: string | null }) {
  const label = lastUsedLabel(iso);
  if (label === null) return <When iso={iso} />;
  return (
    <span style={s.muted} title={label.title}>
      {label.text}
    </span>
  );
}

/**
 * What to say when `lastUsedAt` is absent — `null` meaning "render the date".
 *
 * Exported and pure so the wording is pinned by a test rather than by review.
 * The failure being prevented is one word: a portal that says **Never** about a
 * passkey somebody used this morning, to a staff member who is about to repeat
 * it to that person. Every passkey registered before 2026-08-25 starts absent
 * and stays absent until its next sign-in, so "never" would be wrong for the
 * majority of them on the day this shipped.
 */
export function lastUsedLabel(iso: string | null): { text: string; title: string } | null {
  if (iso) return null;
  return {
    text: 'Not recorded',
    title:
      'Not recorded. This was first tracked on 25 August 2026, so a passkey in regular use can still show as not recorded until its next sign-in.',
  };
}

function Notes({ userId }: { userId: string }) {
  /**
   * SORTED BY THE SERVER (RidgeWill/auth#286).
   *
   * The endpoint returns the newest notes up to its page limit, so reversing the
   * fetched rows would order the page rather than the list — "oldest first"
   * would show the oldest of the newest fifty, which is wrong and looks right.
   * Same rule as the Users search being server-side.
   */
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const { state, reload } = useAsync(() => adminApi.notes(userId, { sort }), [userId, sort]);
  /**
   * The role vocabulary, for the author's tooltip.
   *
   * Its own request rather than something the notes endpoint embeds: it is the
   * same answer for every row and it changes when the auth service is released,
   * not when a note is written. A failure is silent by design — `roleLabel`
   * falls back to the slug, which is still a true statement about the author.
   */
  const vocabulary = useAsync(() => adminApi.roles());
  const labels = vocabulary.state.status === 'ready' ? vocabulary.state.data.labels : undefined;
  const [adding, setAdding] = useState(false);
  const action = useAction();
  const mayWriteNotes = useCan('note:write');

  return (
    <Panel
      title="Notes"
      /*
        Corrected 2026-08-28. This said "the activity trail is the record: adding
        or removing a note is logged there, the text is not" — which stopped being
        true at org-admin#44, when the note types started carrying the note's own
        prose. The old wording had it exactly backwards on the one point that
        decides whether somebody treats a note as disposable.
      */
      info={
        <InfoBubble label="What notes are for">
          Staff scratch space: why somebody called, what was verified, what to watch for. Deletable
          &mdash; but not lost. Adding and removing a note are both recorded in the activity trail,
          and the text goes with them, so a deleted note is still readable there by anybody who can
          read this account&rsquo;s history.
        </InfoBubble>
      }
      /*
        THE ADD ACTION IS IN THE HEADER, and opens a dialog (Jon, 2026-08-28).

        It was a textarea and a button under the table, which put the thing
        somebody came to do below however many notes the account already had —
        the same mistake the create actions on every list screen were moved out of
        (org-admin#7). On a busy account it was a scroll away, and it grew further
        away the more the account was worked on.

        A modal rather than an inline form in the header: a note runs to
        paragraphs, and a textarea in a panel header either squashes the writing
        space or pushes the table down the page every time it is opened.
      */
      action={
        <Button
          disabled={action.running || !mayWriteNotes}
          title={mayWriteNotes ? undefined : whyDisabled('writing notes')}
          onClick={() => setAdding(true)}
        >
          + Add note
        </Button>
      }
    >
      {action.outcome ? (
        <PanelBody>
          <OutcomeNote outcome={action.outcome} />
        </PanelBody>
      ) : null}

      <AsyncBoundary state={state}>
        {(d) =>
          d.notes.length === 0 ? (
            <EmptyState
              title="No notes on this account"
              suggestion="Add one: why somebody called, what was verified, what to watch for."
            />
          ) : (
            <>
              <Table
                columns={[
                  <SortHeader key="when" sort={sort} onSort={setSort} />,
                  'Author',
                  'Note',
                  '',
                ]}
                actionsLast
              >
                {d.notes.map((n) => (
                  <tr key={n.id}>
                    {/*
                      TOP-ALIGNED, all four (Jon, 2026-08-28). The note column can
                      run to several lines while the others are three words, and
                      centred those short cells float in the middle of a tall row
                      and stop marking where it starts — so two long notes in
                      sequence read as one block of prose with grey text loose
                      beside it.
                    */}
                    <Td top>
                      <When iso={n.createdAt} />
                    </Td>
                    <Td top>
                      <NoteAuthor author={n.author} labels={labels} />
                    </Td>
                    {/* Rendered as text. The server stores plain text and says so;
                        rendering it as markup would make a note an injection point
                        aimed at the next admin to open the account. */}
                    <Td top>{n.body}</Td>
                    <Td action top>
                      {/*
                        A HOLD, not a click (Jon, 2026-08-28).

                        A note is the only thing on this screen that can be
                        destroyed outright. The activity trail keeps the deletion
                        and the text, but the row here is the live copy and
                        nothing on this screen brings it back — and the control
                        sits at the end of a row in a list, which is the
                        neighbourhood the hold exists for.
                      */}
                      <HoldButton
                        disabled={action.running}
                        title="Hold for a second and a half to delete this note. The activity trail keeps a record of the deletion and of the text."
                        onHold={() =>
                          void action
                            .run(async () => {
                              await adminApi.deleteNote(n.id);
                              return 'Note deleted. The activity trail keeps the text.';
                            })
                            .then(() => reload())
                        }
                      >
                        Delete
                      </HoldButton>
                    </Td>
                  </tr>
                ))}
              </Table>

              {/*
                The page limit, said out loud when it bites.

                The endpoint caps at fifty and this screen does not page, so a
                heavily-noted account would silently show a subset — and with a
                sortable date column that subset is "the newest fifty" or "the
                oldest fifty" depending on the control, which is exactly the kind
                of quiet truncation that makes a sort look broken.
              */}
              {d.total > d.notes.length ? (
                <PanelBody>
                  <Note>
                    Showing {count(d.notes.length)} of {count(d.total)}, {d.sort} first. Switch the
                    order to read the other end.
                  </Note>
                </PanelBody>
              ) : null}
            </>
          )
        }
      </AsyncBoundary>

      {adding ? (
        <AddNote
          userId={userId}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </Panel>
  );
}

/**
 * The date column header, which sorts.
 *
 * A button inside the `th` rather than a clickable `th`: the header is a label
 * for a column and a control at the same time, and only one of those should be
 * reachable by keyboard as an action. `aria-sort` is on the button because the
 * `Table` owns the cell.
 *
 * The arrow is the state. A header that changes nothing visible when pressed is
 * indistinguishable from one that did not register the press, which is the same
 * argument the hold button's fill makes.
 */
function SortHeader({
  sort,
  onSort,
}: {
  sort: 'newest' | 'oldest';
  onSort: (next: 'newest' | 'oldest') => void;
}) {
  const newest = sort === 'newest';
  return (
    <button
      type="button"
      onClick={() => onSort(newest ? 'oldest' : 'newest')}
      aria-label={`When, ${newest ? 'newest' : 'oldest'} first. Sort ${newest ? 'oldest' : 'newest'} first.`}
      title={`Sorted ${newest ? 'newest' : 'oldest'} first. Click for ${newest ? 'oldest' : 'newest'} first.`}
      style={s.sortHeader}
    >
      When
      <span aria-hidden style={{ fontSize: '.7em' }}>
        {newest ? '▼' : '▲'}
      </span>
    </button>
  );
}

/**
 * Writing a note, in a dialog.
 *
 * A picker or a confirmation is what modals are for after 2026-08-28, and this
 * is neither — it is a form. It is a dialog anyway for the reason the header
 * comment gives: the alternative puts the writing space below every note already
 * on the account.
 *
 * Its own component so the draft is DISCARDED with the dialog. Held in the panel
 * it would survive being closed, and a half-written note reappearing days later
 * on somebody else's account is worse than losing one somebody chose to abandon.
 */
function AddNote({
  userId,
  onClose,
  onAdded,
}: {
  userId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [body, setBody] = useState('');
  const action = useAction();

  return (
    <Modal
      title="Add a note"
      subtitle="Recorded in the activity trail as well, text included."
      onClose={onClose}
      width={520}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !body.trim()}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.addNote(userId, body.trim());
                  return 'Note added.';
                })
                .then(onAdded)
            }
          >
            {action.running ? 'Saving…' : 'Add note'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field label="Note">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          autoFocus
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>
    </Modal>
  );
}

/**
 * Who wrote a note, as an EMAIL that goes to their account.
 *
 * ── The display name is gone, and the link is new (Jon, 2026-08-28) ──
 *
 * The cell rendered "jon@ridgewill.com  Jon McMillan", which is one identity
 * written twice. The email is the one staff use to each other and the one that
 * matches a log line, so it is the one that stays.
 *
 * It is a link because the author is a record on this very screen, and the
 * question after "who wrote this" is usually "what else have they touched".
 *
 * ── The hover says what they COULD DO, not just who they are ─────────
 *
 * A note is read during an escalation, and "was this an observation or a
 * decision" is the question that actually matters. A Support Admin cannot change
 * any configuration, so a note from one is a report; the same words from a
 * Global Admin may be the record of an action. The role is the only thing on the
 * row that answers it.
 *
 * `title`, not an `InfoBubble`. The bubble exists for a paragraph somebody may
 * need to read and is a control you click; this is a few words qualifying one
 * cell, which is exactly what `title` is for and what the bubble's own comment
 * says it should stay for.
 *
 * ── Every fallback is a statement rather than a blank ────────────────
 *
 * No author means the row predates author tracking or was written by the
 * system. No email means the author's own account has been closed and its PII
 * cleared — which is worth saying, because the note is still evidence and the
 * person who wrote it is still identifiable by id to anyone who needs it.
 */
function NoteAuthor({
  author,
  labels,
}: {
  author: UserNote['author'];
  labels?: RoleLabels;
}) {
  if (!author) return <span style={{ color: C.muted }}>system</span>;

  /**
   * THE LABEL, and only the label (Jon, 2026-08-28).
   *
   * This carried each role's one-sentence description as well, which turned a
   * three-word answer into a paragraph hovering over the table. The question the
   * tooltip exists for is "which admin is this" — "Global Admin" answers it, and
   * what a Global Admin can do is not something a reader of one note needs
   * explained to them every time they check who wrote it.
   */
  const roles = splitRoles(author.role);
  const title = roles.length
    ? roles.map((r) => roleLabel(r, labels)).join(', ')
    : /* An author with no role at all is not a normal state for somebody who
         could write a note — every admin role is a role. Saying so beats an
         empty tooltip, which reads as "the hover is broken". */
      'No platform role';

  if (!author.email) {
    return (
      <span style={{ color: C.muted }} title={title}>
        closed account
      </span>
    );
  }

  return (
    <a href={`#/users/${encodeURIComponent(author.id)}`} title={title} style={s.authorLink}>
      {author.email}
    </a>
  );
}

const s = {
  section: { display: 'grid', gap: '.7rem' },
  muted: { margin: 0, fontSize: '.83rem', color: C.muted, lineHeight: 1.5 },
  /** `auto-fit` with a floor rather than fixed columns: the facts are short and
   *  a narrow window should fold them rather than squeeze an account id. */
  facts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: '.9rem 1.4rem',
  },
  factLabel: {
    fontSize: '.72rem',
    letterSpacing: '.06em',
    textTransform: 'uppercase' as const,
    color: C.muted,
    fontWeight: 640,
  },
  /** Wraps rather than overflowing. An id is one long unbroken token, so the
   *  default would push the panel wider than the column on a narrow window. */
  id: { fontSize: '.82rem', color: C.textMid, wordBreak: 'break-all' as const },
  /** Underlined on hover only, like every other row link in the portal. The
   *  dotted cue for the tooltip would fight it, so the tooltip is discovered the
   *  way tooltips are. */
  authorLink: { color: C.brand, fontWeight: 600, textDecoration: 'none' },
  /** Inherits the `th`'s type so the column reads as a header that happens to be
   *  pressable, rather than as a control that happens to be in a header. */
  sortHeader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '.35rem',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    letterSpacing: 'inherit',
    textTransform: 'inherit' as const,
    cursor: 'pointer',
  },
  agent: {
    display: 'inline-block',
    maxWidth: 220,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'bottom' as const,
    fontSize: '.8rem',
    color: C.textMid,
  },
};

/** The facts grid, shared with the Family tab (org-admin#187). */
export const factsStyle = s.facts;
